import type { AdapterFetchOptions, AdapterResult, MarketSourceAdapter } from '@/types/adapters';
import type { NormalizedMarketRow } from '@/types/market';
import { appConfig } from '@/config/app';
import { featureFlags } from '@/config/featureFlags';
import { SOURCE_REGISTRY } from '@/data/sourceRegistry';

const EXPECTED_FIELDS = 5; // medianSalePrice, medianDaysOnMarket, activeListings, newListings, pricePerSqft (not on NormalizedMarketRow -- see note below)

interface CountyMapRow {
  fips: string;
  countyName: string;
  periodLabel: string;
  publishedAt: string;
  medianListingPrice: number | null;
  medianListingPricePrevPeriod: number | null;
  medianDaysOnMarket: number | null;
  medianDaysOnMarketPrevPeriod: number | null;
  activeListings: number | null;
  activeListingsPrevPeriod: number | null;
  newListings: number | null;
  pricePerSqft: number | null;
}

interface CountyMapResponse {
  source: string;
  asOf: string | null;
  publishedAt: string | null;
  fetchedAt: string;
  countyCount: number;
  counties: CountyMapRow[];
  warnings: string[];
}

// ZIP-map row/response shapes -- field-for-field the same as county's, just
// keyed by `zip` instead of `fips` (see backend's
// realtorComZipMapAdapter.js and GET /api/stats/zip-map, which mirrors
// getCountyMap intentionally).
interface ZipMapRow {
  zip: string;
  zipName: string | null;
  periodLabel: string;
  publishedAt: string;
  medianListingPrice: number | null;
  medianListingPricePrevPeriod: number | null;
  medianDaysOnMarket: number | null;
  medianDaysOnMarketPrevPeriod: number | null;
  activeListings: number | null;
  activeListingsPrevPeriod: number | null;
  newListings: number | null;
  pricePerSqft: number | null;
}

interface ZipMapResponse {
  source: string;
  asOf: string | null;
  publishedAt: string | null;
  fetchedAt: string;
  zipCount: number;
  zips: ZipMapRow[];
  warnings: string[];
}

async function fetchJson<T>(path: string, signal?: AbortSignal): Promise<T | null> {
  for (const base of appConfig.mayApiBaseUrlCandidates) {
    try {
      const res = await fetch(`${base}${path}`, { signal, mode: 'cors' });
      if (res.ok) return (await res.json()) as T;
    } catch {
      // try next candidate -- network error, CORS, or that base isn't up
    }
  }
  return null;
}

function toRow(c: CountyMapRow): NormalizedMarketRow {
  const filled = [c.medianListingPrice, c.medianDaysOnMarket, c.activeListings, c.newListings, c.pricePerSqft].filter(
    (v) => v !== null,
  ).length;

  return {
    geoLevel: 'county',
    geoId: c.fips,
    geoName: c.countyName,
    sourceId: 'realtor-com-live',
    sourceLabel: SOURCE_REGISTRY['realtor-com-live'].label,
    periodLabel: c.periodLabel,
    publishedAt: c.publishedAt,
    retrievedAt: new Date().toISOString(),
    // Realtor.com's public county file is *listing*-side (active/new
    // listings), not closed-sale transactions -- there is no separate
    // "listing price" field on NormalizedMarketRow, so this is mapped onto
    // medianSalePrice like the backend's own realtorComAdapter.js does,
    // with the same caveat: it's the closest available proxy, not a
    // literal median *sale* price. closedSales/pendingSales/
    // saleToListRatioPct/monthsOfInventory are left undefined rather than
    // guessed at, since this source doesn't report them.
    medianSalePrice: c.medianListingPrice ?? undefined,
    medianSalePricePrevPeriod: c.medianListingPricePrevPeriod ?? undefined,
    medianSalePriceYoyPct: undefined,
    medianDaysOnMarket: c.medianDaysOnMarket ?? undefined,
    medianDaysOnMarketPrevPeriod: c.medianDaysOnMarketPrevPeriod ?? undefined,
    activeListings: c.activeListings ?? undefined,
    activeListingsPrevPeriod: c.activeListingsPrevPeriod ?? undefined,
    newListings: c.newListings ?? undefined,
    fieldCompleteness: filled / EXPECTED_FIELDS,
  };
}

function toZipRow(z: ZipMapRow): NormalizedMarketRow {
  const filled = [z.medianListingPrice, z.medianDaysOnMarket, z.activeListings, z.newListings, z.pricePerSqft].filter(
    (v) => v !== null,
  ).length;

  return {
    geoLevel: 'zip',
    geoId: z.zip,
    geoName: z.zipName ? `${z.zip} (${z.zipName})` : z.zip,
    sourceId: 'realtor-com-live',
    sourceLabel: SOURCE_REGISTRY['realtor-com-live'].label,
    periodLabel: z.periodLabel,
    publishedAt: z.publishedAt,
    retrievedAt: new Date().toISOString(),
    // Same listing-vs-sale-price caveat as toRow() above -- this is
    // Realtor.com's listing-side median, mapped onto medianSalePrice as the
    // closest available proxy field on NormalizedMarketRow.
    medianSalePrice: z.medianListingPrice ?? undefined,
    medianSalePricePrevPeriod: z.medianListingPricePrevPeriod ?? undefined,
    medianSalePriceYoyPct: undefined,
    medianDaysOnMarket: z.medianDaysOnMarket ?? undefined,
    medianDaysOnMarketPrevPeriod: z.medianDaysOnMarketPrevPeriod ?? undefined,
    activeListings: z.activeListings ?? undefined,
    activeListingsPrevPeriod: z.activeListingsPrevPeriod ?? undefined,
    newListings: z.newListings ?? undefined,
    fieldCompleteness: filled / EXPECTED_FIELDS,
  };
}

/**
 * Real, live data adapter -- fetches GET /api/stats/county-map (and, once
 * ENABLE_ZIP_LAYER is on, GET /api/stats/zip-map too) from the actual MAY
 * Assistant backend (autova-backend, Real Data Stats Layer), which itself
 * pulls Realtor.com's public CSVs server-side (avoiding the browser
 * CORS/rate-limit issues a direct client fetch to econdata.s3...amazonaws.com
 * would hit) and maps county/ZIP names to FIPS/ZCTA codes. This is the map's
 * primary source -- see sourceRegistry.ts and featureFlags.ts's
 * ENABLE_SAMPLE_FALLBACK for how this replaces the old always-on synthetic
 * sample CSVs.
 *
 * Both requests share the same sourceId ('realtor-com-live') since they're
 * the same underlying provider at two geography levels, not two different
 * sources -- geoLevel on each row (not sourceId) is what tells scoring/
 * merge/rendering code which geography a row belongs to, and that logic is
 * already fully generic across geoLevel (see engine/scoring, data/pipeline).
 * The ZIP fetch is gated behind ENABLE_ZIP_LAYER so it doesn't run (and
 * doesn't ask the backend to do the multi-MB ZIP CSV parse) while the layer
 * itself is off.
 */
export const mayBackendAdapter: MarketSourceAdapter = {
  meta: SOURCE_REGISTRY['realtor-com-live'],

  async fetch(options?: AdapterFetchOptions): Promise<AdapterResult> {
    const warnings: string[] = [];
    const rows: NormalizedMarketRow[] = [];

    const countyData = await fetchJson<CountyMapResponse>('/api/stats/county-map', options?.signal);
    if (!countyData) {
      warnings.push('realtor-com-live: could not reach the backend at any configured API base URL -- no real county data this refresh.');
    } else {
      warnings.push(...(countyData.warnings || []).map((w) => `realtor-com-live: ${w}`));
      rows.push(...countyData.counties.map(toRow));
    }

    if (featureFlags.ENABLE_ZIP_LAYER) {
      const zipData = await fetchJson<ZipMapResponse>('/api/stats/zip-map', options?.signal);
      if (!zipData) {
        warnings.push('realtor-com-live: could not reach the backend for ZIP data this refresh -- ZIP layer will show no data.');
      } else {
        warnings.push(...(zipData.warnings || []).map((w) => `realtor-com-live (zip): ${w}`));
        rows.push(...zipData.zips.map(toZipRow));
      }
    }

    return { rows, warnings, isSampleData: false };
  },
};
